import { createContext, useContext, useEffect, useMemo } from 'react';
import { StoreApi, useStore } from 'zustand';

export interface StepInfo {
  id: string;
  validate?: () => Promise<boolean> | boolean;
}

export interface MultistepActions {
  addStep: (step: StepInfo) => void;
  removeStep: (id: string) => void;
  skipToStep: (id: string) => void;
  next: () => Promise<void>;
  previous: () => void;
  finish: () => Promise<void>;
  canGoNext: boolean;
  canGoPrevious: boolean;
}

export interface MultistepState {
  steps: Record<string, StepInfo>;
  current?: string;
  loading?: boolean;
}
export const MultistepContext = createContext<StoreApi<MultistepState & MultistepActions> | null>(null);

export function useMultistep<U>(selector: (state: MultistepState & MultistepActions) => U) {
  const context = useContext(MultistepContext);
  if (!context) {
    throw new Error('useMultistep must be used within a MultistepProvider');
  }
  return useStore(context, selector);
}

export enum StepStatus {
  Completed = 'completed',
  Pending = 'pending',
  Current = 'current'
}

const isCompleted = (id: string, current: string, keys: string[]) => keys.indexOf(id) < keys.indexOf(current);
const isPending = (id: string, current: string, keys: string[]) => keys.indexOf(id) > keys.indexOf(current);
const isCurrent = (id: string, current: string) => id === current;

export const useStep = (id: string, validate?: () => Promise<boolean> | boolean) => {
  const { addStep, current, steps } = useMultistep(state => state);

  useEffect(() => {
    addStep({ id, validate });
  }, [validate, id, addStep]);

  const status = useMemo(() => {
    if (!current) return;
    const keys = Object.keys(steps);
    if (isCurrent(id, current)) return StepStatus.Current;
    if (isCompleted(id, current, keys)) return StepStatus.Completed;
    if (isPending(id, current, keys)) return StepStatus.Pending;
  }, [current, id, steps]);

  return { status };
};

export const useControls = () => {
  const { next, previous, finish, canGoNext, canGoPrevious, skipToStep } = useMultistep(state => state);
  return { next, previous, finish, canGoNext, canGoPrevious, skipToStep };
};
